/* eslint-disable max-len */
/* eslint-disable prefer-destructuring */
import {Utils, ExceptionService} from '@busuu/legacy-core';
import FormUtils from 'helpers/form-utils.js';
import AccountFlyover from 'registration/registration-busuu/account-flyover-class.js';
import ConfigService from 'config/config-service.js';
import MobileService from 'mobile/mobile-service.ts';
import TranslationsService from 'common/translations.service.ts';
import QueryParameters from 'helpers/query-parameters.js';
import ReferralService from 'referral/referral-service.js';
import RegistrationService from 'registration/registration-service.js';
import CaptchaService from 'captcha/captcha-service.js';
import RegistrationTracking from 'tracking/pages/tracking-register.js';
import PasswordVisibilityToggle from 'registration/registration-busuu/password-visibility-toggle-class.js';
import {isInCrossPlatformCountrySpecificTermsAndConditionsRegisterPageExperiment} from '@busuu/experiments';
import Tracking from 'tracking/tracking-controller.ts';

let accountFlyover;

let form;
let errorForm;
let successForm;
let buttonSubmit;
let inputName;
let inputPassword;
let checkOptInPromotions;
let hiddenInterfaceLang;
let hiddenLearningLang;
let formLoader;
let socialLoginButtons;
let backButton;
let inputEmail;
let optInPromotions;
let emailGroup;
let passwordGroup;
let nameGroup;
let isInExperiment;

const accessType = 'BUSUU-EMAIL';
const filename = 'registration-busuu.js';
const validatedGroupClass = 'auth-form__group--validated';

const logError = (error) => {
    ExceptionService.handle('error', {
        filename,
        data: error,
    });
};

const getRequestParams = (countryCode) => {
    const institutionParams = QueryParameters.getInstitutionParameters();
    const referralCode = ReferralService.getReferralUserCode();
    const perkToken = ReferralService.getPerkToken();
    const timezone = RegistrationService.getTimezoneOffset();
    const platformSource = RegistrationService.getPlatformSource();
    const visitorId = QueryParameters.getVisitorIdParameter();
    const interfaceLanguage = RegistrationService.getInterfaceLanguage(hiddenInterfaceLang.value, countryCode);

    const requestParams = {
        name: inputName.value,
        email: inputEmail.value,
        password: inputPassword.value,
        interface_language: interfaceLanguage,
        learning_language: hiddenLearningLang.value,
        speaking_language: interfaceLanguage,
        opt_in_promotions: optInPromotions,
        country: countryCode,
        platform: platformSource,
        ...(referralCode && {
            referral_token: referralCode,
        }),
        ...(perkToken && {
            perk_token: perkToken,
        }),
        ...(timezone && {timezone}),
        ...(institutionParams.institution_code && {
            institution_data: {
                name: institutionParams.institution_name,
                code: institutionParams.institution_code,
            },
        }),
        ...(visitorId &&
            MobileService.visitorIdIsUUID(visitorId) && {
                visitor_id: visitorId,
            }),
    };
    return requestParams;
};

const redirect = (url) => {
    window.location.href = url;
};

// Change the content of the title
const changeFormTitle = (type) => {
    const title = Utils.getElementByClass('auth-form__header-title');
    if (!title) {
        return false;
    }
    const languageTranslation = TranslationsService.getTranslation(`LANGUAGE_${hiddenLearningLang.value}`);

    let emailRegistrationTitle;
    switch (type) {
        case 'ERROR':
            emailRegistrationTitle = TranslationsService.getTranslation('SOMETHING_DOES_NOT_LOOK_RIGHT');
            break;
        case 'DETAILS':
            emailRegistrationTitle = TranslationsService.getTranslation('ADD_YOUR_DETAILS');
            break;
        case 'EDU':
            emailRegistrationTitle = 'Sign up with your .edu email';
            break;
        case 'JOIN':
            emailRegistrationTitle = TranslationsService.getTranslation('JOIN_OUR_COMMUNITY');
            break;
        case 'CONFIRMATION':
            emailRegistrationTitle = 'Thank you for registering!';
            break;

        default:
            emailRegistrationTitle = TranslationsService.getAndReplaceTranslation(
                'SIGN_UP_AND_START_LEARNING',
                languageTranslation
            );
    }

    Utils.setText(title, emailRegistrationTitle);
};

const onRegistrationSuccess = ({user, registrationResponse}) => {
    const redirectURL = RegistrationService.getAfterRegistrationURL({
        defaultURL: form.getAttribute('action'),
        registrationResponse,
        user,
    });

    if (registrationResponse.backendRedirection) {
        return redirect(redirectURL);
    }

    return RegistrationTracking.sendRegistrationSuccessTracking(accessType, {
        email_opt_in: optInPromotions,
    }).then(redirect.bind(null, redirectURL));
};

const onRegistrationWithNonceSuccess = (response) => {
    return RegistrationTracking.sendRegistrationWithNonceSuccessTracking(accessType, {
        email_opt_in: optInPromotions,
    }).then(
        MobileService.callbackMobileAppAfterAuthenticationOrRegistration.bind(null, {
            accessType,
            source: 'register',
            ...response,
        })
    );
};

/**
 * Error handling
 */
const scrollToError = () => {
    // Try to scroll to the error form first, fallback to top of page
    if (errorForm && !errorForm.classList.contains('hidden')) {
        errorForm.scrollIntoView({behavior: 'smooth', block: 'start'});
    } else {
        window.scrollTo({top: 0, behavior: 'smooth'});
    }
};

const showError = (type) => {
    switch (type) {
        case 'EMAIL_ALREADY_USED':
            return accountFlyover.show();
        case 'INVALID_EDU_EMAIL':
            changeFormTitle('ERROR');
            errorForm.innerHTML =
                'For US only: Sign up with an email ending in ’.edu’ to claim 3 months free Busuu Premium. If your email doesn’t end in ’.edu’, you can still use the free version of Busuu <a href="/choose-your-language">here</a>.';
            FormUtils.showElement(errorForm);
            scrollToError();
            break;
        case 'TERMS_NOT_ACCEPTED':
            errorForm.innerHTML = TranslationsService.getTranslation('PLEASE_AGREE_TO_THE_TERMS_AND_CONDITIONS');
            FormUtils.showElement(errorForm);
            scrollToError();
            break;
        default:
            changeFormTitle('ERROR');
            errorForm.innerHTML = RegistrationService.getErrorMessage(type);
            FormUtils.showElement(errorForm);
            scrollToError();
    }
};

/**
 * Show success
 */
const showConfirmationRequired = () => {
    changeFormTitle('CONFIRMATION');
    successForm.innerHTML =
        "Please verify your details. Click the link in the email we've sent to complete registration.";
    FormUtils.showElement(successForm);
    window.scrollTo(0, 0);
    FormUtils.stopButtonLoading(buttonSubmit);
};

const displayUIError = (type) => {
    showError(type);
    FormUtils.stopButtonLoading(buttonSubmit);

    // Re-enable terms checkbox on error
    const checkReadTerms = Utils.getById('register-institution-terms');
    if (checkReadTerms) {
        checkReadTerms.disabled = false;
    }
};

const onCodeException = (error) => {
    logError(error);
    showError('RUNTIME_ERROR');
    FormUtils.hideElement(formLoader);
    FormUtils.showElement(form);
};

const onRegistrationError = ({type = 'UNKNOWN_TYPE'} = {}) => {
    displayUIError(type);
    const params = {error: type};
    RegistrationTracking.sendRegistrationErrorTracking(accessType, params);
};

const onRegistrationWithNonceError = ({type = 'UNKNOWN_TYPE'} = {}) => {
    displayUIError(type);
    const params = {error: type};
    RegistrationTracking.sendRegistrationWithNonceErrorTracking(accessType, params);
};

const onSubmitRequest = ({country, isEduSignup}) => {
    optInPromotions = RegistrationService.canOptInPromotions(country, checkOptInPromotions);

    const onRequestSuccess = (requestParams, response) => {
        const {user, registrationResponse} = response;
        if (registrationResponse.email_verification_required) {
            return showConfirmationRequired();
        }
        return onRegistrationSuccess({user, registrationResponse});
    };

    const onCaptchaSuccess = ({token}) => {
        const requestParams = getRequestParams(country);
        const emailExploded = requestParams?.email?.split('.');
        const isValidEduEmail =
            emailExploded[emailExploded.length - 1] === 'edu' ||
            requestParams.email === 'karthika+008@busuu.com' ||
            requestParams.email === 'karthika+009@busuu.com' ||
            requestParams.email === 'karthika+010@busuu.com' ||
            requestParams.email === 'achraf+10@busuu.com';

        if (token) {
            requestParams.captcha_token = token;
        }

        if (isEduSignup) {
            if (isValidEduEmail) {
                RegistrationService.createUser(requestParams)
                    .then(onRequestSuccess.bind(null, requestParams))
                    .catch(onRegistrationError);
            } else {
                return onRegistrationError({type: 'INVALID_EDU_EMAIL'});
            }
            return false;
        }

        if (MobileService.isAuthenticationOrRegistrationMobileFlow()) {
            if (isValidEduEmail) {
                RegistrationService.createUser(requestParams)
                    .then(onRequestSuccess.bind(null, requestParams))
                    .catch(onRegistrationError);
            } else {
                RegistrationService.createUserAndGetNonce(requestParams)
                    .then(onRegistrationWithNonceSuccess)
                    .catch(onRegistrationWithNonceError);
            }
            return false;
        }

        RegistrationService.createUser(requestParams)
            .then(onRequestSuccess.bind(null, requestParams))
            .catch(onRegistrationError);
    };

    const onCaptchaError = ({type = 'UNKNOWN_TYPE'} = {}) => {
        displayUIError(type);
    };

    const captchaParams = {
        endpointType: 'register',
        containerID: 'register-recaptcha',
    };

    return CaptchaService.initializeAndRenderChallenge(captchaParams).then(onCaptchaSuccess).catch(onCaptchaError);
};

/**
 * Validates that the user has accepted the terms and conditions
 * Shows error message if not accepted
 * @return {boolean} true if terms are accepted, false otherwise
 */
const validateTermsAcceptance = () => {
    // If user is not in the experiment, skip validation (DECLARE_READ_ACCEPT message is shown)
    if (!isInExperiment) {
        return true;
    }
    const checkReadTerms = Utils.getById('register-institution-terms');
    const termsWrapper = Utils.getById('register-institution-terms-wrap');
    const termsLabel = termsWrapper ? termsWrapper.querySelector('.auth-form__opt-in-label') : null;
    const termsErrorMessage = Utils.getById('terms-error-message');

    if (!checkReadTerms || !checkReadTerms.checked) {
        // Add error classes and show error message
        if (termsWrapper) {
            Utils.addClass(termsWrapper, 'auth-form__opt-in-wrapper--error');
        }
        if (termsLabel) {
            Utils.addClass(termsLabel, 'auth-form__opt-in-label--error');
        }
        if (termsErrorMessage) {
            FormUtils.showElement(termsErrorMessage);
        }
        // Scroll to the terms section specifically
        if (termsWrapper) {
            termsWrapper.scrollIntoView({behavior: 'smooth', block: 'start'});
        } else {
            window.scrollTo({top: 0, behavior: 'smooth'});
        }
        return false;
    }

    // Remove error classes and hide error message if terms are accepted
    if (termsWrapper) {
        Utils.removeClass(termsWrapper, 'auth-form__opt-in-wrapper--error');
    }
    if (termsLabel) {
        Utils.removeClass(termsLabel, 'auth-form__opt-in-label--error');
    }
    if (termsErrorMessage) {
        FormUtils.hideElement(termsErrorMessage);
    }
    return true;
};

const onFormSubmit = (country, e) => {
    if (e) {
        e.preventDefault();
    }

    // Validate terms acceptance before submitting
    if (!validateTermsAcceptance()) {
        return;
    }

    // Disable terms checkbox during submission
    const checkReadTerms = Utils.getById('register-institution-terms');
    if (checkReadTerms) {
        checkReadTerms.disabled = true;
    }

    const isEduSignup = Boolean(form.getAttribute('data-edu'));
    FormUtils.setButtonLoading(buttonSubmit);
    FormUtils.hideElement(errorForm);
    onSubmitRequest({country, isEduSignup});
};

const initOptInPromotions = (enabled2FA, countryCode) => {
    const checkOptInPromotionsFormGroup = Utils.getElementByClass('js-communications-opt-in-toggle');

    if (!checkOptInPromotionsFormGroup) return;

    Utils.removeClass(checkOptInPromotionsFormGroup, 'hidden');
    FormUtils.toggleElement(checkOptInPromotionsFormGroup, !enabled2FA);

    if (!enabled2FA) {
        FormUtils.markCheckBox(checkOptInPromotions, RegistrationService.getOptInPromotionsState(countryCode));
    }
};

const initReadTerms = () => {
    const checkReadTermsFormGroup = Utils.getById('register-institution-terms-wrap');
    const checkReadTerms = Utils.getById('register-institution-terms');
    const termsErrorMessage = Utils.getById('terms-error-message');

    if (isInExperiment) {
        // Show the terms checkbox for users in the experiment
        FormUtils.showElement(checkReadTermsFormGroup);

        // Add event listener to remove error state when checkbox is checked
        if (checkReadTerms) {
            checkReadTerms.addEventListener('change', () => {
                if (checkReadTerms.checked) {
                    const termsLabel = checkReadTermsFormGroup
                        ? checkReadTermsFormGroup.querySelector('.auth-form__opt-in-label')
                        : null;
                    if (checkReadTermsFormGroup) {
                        Utils.removeClass(checkReadTermsFormGroup, 'auth-form__opt-in-wrapper--error');
                    }
                    if (termsLabel) {
                        Utils.removeClass(termsLabel, 'auth-form__opt-in-label--error');
                    }
                    if (termsErrorMessage) {
                        FormUtils.hideElement(termsErrorMessage);
                    }
                }
            });
        }

        // since mobile flows start directly in email mode and don't go through showEmailForm()
        const isMobileFlow = MobileService.isAuthenticationOrRegistrationMobileFlow();
        if (isMobileFlow) {
            if (checkReadTerms) {
                checkReadTerms.checked = true;
                checkReadTerms.disabled = false;
            }
        }
    } else {
        // Hide the terms checkbox for users not in the experiment
        if (checkReadTermsFormGroup) {
            FormUtils.hideElement(checkReadTermsFormGroup);
            // Also add hidden class as backup
            Utils.addClass(checkReadTermsFormGroup, 'hidden');
        }

        if (checkReadTerms) {
            checkReadTerms.checked = true;
            checkReadTerms.disabled = false;
        }

        // Show the DECLARE_READ_ACCEPT message instead
        const declareReadAcceptMessage = Utils.getById('read-receipt-description');
        if (declareReadAcceptMessage) {
            FormUtils.showElement(declareReadAcceptMessage);
        }
    }
};

const initSocialLoginButtons = (enabled2FA) => {
    FormUtils.toggleElement(socialLoginButtons, !enabled2FA);
};

const initSocialButtonsVisibility = () => {
    const socialFormLink = Utils.getById('register-use-social');
    const mailFormLink = Utils.getById('register-use-email');
    const promoOptInCheckbox = Utils.getById('communications-opt-in-toggle');

    const hideErrors = () => {
        errorForm.innerHTML = '';
        FormUtils.hideElement(errorForm);
    };

    const toggleSocialButtonsVisibility = () => {
        Utils.getElementsByClass('toggle-social-signup').forEach((element) => Utils.toggleClass(element, 'hidden'));
    };

    const showSocialForm = (e) => {
        if (e) {
            e.preventDefault();
        }

        if (!validateTermsAcceptance()) {
            return;
        }

        initReadTerms();
        toggleSocialButtonsVisibility();
        hideErrors();

        // Don't force the terms checkbox to be checked
        const checkReadTerms = Utils.getById('register-institution-terms');
        if (checkReadTerms) {
            checkReadTerms.disabled = false;
        }

        FormUtils.showElement(mailFormLink);
        FormUtils.showElement(socialLoginButtons);
    };

    const showEmailForm = (e) => {
        if (e) {
            e.preventDefault();
        }

        if (!validateTermsAcceptance()) {
            return;
        }

        initReadTerms();
        toggleSocialButtonsVisibility();
        hideErrors();

        // Don't force the terms checkbox to be checked
        const checkReadTerms = Utils.getById('register-institution-terms');
        if (checkReadTerms) {
            checkReadTerms.disabled = false;
        }

        const title = 'JOIN';

        // Change the title element class for email form
        const titleElement = Utils.getElementByClass('auth-form__header-title-email-form');
        if (titleElement) {
            Utils.removeClass(titleElement, 'auth-form__header-title-email-form');
            Utils.addClass(titleElement, 'auth-form__header-title');
        }

        changeFormTitle(title);
        FormUtils.hideElement(mailFormLink);
        FormUtils.hideElement(socialLoginButtons);

        if (checkOptInPromotions && checkOptInPromotions.checked) {
            FormUtils.hideElement(promoOptInCheckbox);
        }
    };

    function toggleElementAndAddClick(element, callback) {
        if (element) {
            Utils.toggleClass(element, 'hidden');
            element.addEventListener('click', callback);
        }
    }

    if (!socialLoginButtons) {
        return false;
    }

    toggleElementAndAddClick(mailFormLink, showEmailForm);
    toggleElementAndAddClick(socialFormLink, showSocialForm);

    FormUtils.hideElement(socialFormLink);
    Utils.addClass(socialLoginButtons, 'auth-form__socials-wrapper--hide-experiment');
};

const initForm = (enabled2FA, countryCode) => {
    const showOptInPromotionsCheckbox = !RegistrationService.canOptInPromotions(countryCode);

    if (showOptInPromotionsCheckbox) {
        initOptInPromotions(enabled2FA, countryCode);
    }

    initReadTerms();
    initSocialLoginButtons(enabled2FA);

    form.addEventListener('submit', onFormSubmit.bind(null, countryCode));

    initSocialButtonsVisibility();

    FormUtils.hideElement(formLoader);
    FormUtils.showElement(form);
};

const initFormType = () => {
    const onError = () => ({
        twoFactorAuthenticationEnabled: false,
        countryCode: null,
    });

    const onComplete = ({countryCode}) => {
        RegistrationTracking.init(false);
        isInExperiment = isInCrossPlatformCountrySpecificTermsAndConditionsRegisterPageExperiment();

        // Send additional tracking with experiment parameters
        Tracking.send('TERMS_AND_CONDITIONS_VIEWED', {
            has_check_box: isInExperiment,
            country_code: countryCode,
        });

        initForm(false, countryCode);
    };

    return ConfigService.getTwoFactorAuthConfig().catch(onError).then(onComplete).catch(onCodeException);
};

const onBackButtonClick = (e) => {
    if (e) e.preventDefault();

    window.history.back();
};

const onGenerateRandomUser = (e) => {
    if (e) e.preventDefault();
    const {name, email, password} = RegistrationService.getRandomUserData();
    inputName.value = name;
    inputPassword.value = password;
    inputEmail.value = email;
};

const initGenerateRandomUser = () => {
    const generateRandomUserLink = Utils.getById('generate-random-user');
    if (generateRandomUserLink) {
        generateRandomUserLink.addEventListener('click', onGenerateRandomUser);
    }
};

const initBackButton = () => {
    if (backButton) {
        backButton.addEventListener('click', onBackButtonClick);
    }
};

const onPasswordInput = () => {
    return inputPassword.value && inputPassword.value.length >= 6
        ? Utils.addClass(passwordGroup, validatedGroupClass)
        : Utils.removeClass(passwordGroup, validatedGroupClass);
};

const initPasswordEyeToggle = () => {
    if (inputPassword) inputPassword.addEventListener('input', onPasswordInput);
};

const isEmailValid = (email) => {
    const validationRegex =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return validationRegex.test(email);
};

const onEmailInput = () => {
    if (emailGroup && inputEmail.value && isEmailValid(inputEmail.value)) {
        return Utils.addClass(emailGroup, validatedGroupClass);
    }
    return Utils.removeClass(emailGroup, validatedGroupClass);
};

const initEmailVerification = () => {
    if (form.getAttribute('data-edu')) changeFormTitle('EDU');
    if (inputEmail) inputEmail.addEventListener('input', onEmailInput);
};

const isNameValid = (name) => {
    return name.trim().length > 0;
};

const onNameInput = () => {
    if (nameGroup && inputName.value && isNameValid(inputName.value)) {
        return Utils.addClass(nameGroup, validatedGroupClass);
    }
    return Utils.removeClass(nameGroup, validatedGroupClass);
};

const initNameVerification = () => {
    if (inputName) inputName.addEventListener('input', onNameInput);
};

const init = () => {
    form = Utils.getById('register-form');
    if (form) {
        errorForm = Utils.getById('error-generic');
        successForm = Utils.getById('success-generic');

        buttonSubmit = Utils.getById('register-submit');
        checkOptInPromotions = Utils.getById('register-communications-opt-in-toggle');
        inputName = Utils.getById('register-form-name');
        nameGroup = Utils.getById('form-group-name');
        inputEmail = Utils.getById('register-form-email');
        emailGroup = Utils.getById('form-group-email');
        inputPassword = Utils.getById('register-form-password');
        passwordGroup = Utils.getById('form-group-password');
        hiddenInterfaceLang = Utils.getById('register-interface-lang');
        hiddenLearningLang = Utils.getById('register-learning-lang');
        formLoader = Utils.getById('register-form-loader');
        socialLoginButtons = Utils.getById('register-form-socials');
        backButton = Utils.getById('register-back-button');
        accountFlyover = new AccountFlyover();

        new PasswordVisibilityToggle();
        ReferralService.storeReferralUserCode();
        ReferralService.storePerkToken();
        initBackButton();
        initGenerateRandomUser();
        initFormType();
        initPasswordEyeToggle();
        initNameVerification();
        initEmailVerification();
    }
};

init();

// Exported for unit testing and social login modules
export {init, validateTermsAcceptance, showError};
